import { camelCase } from "lodash";
import { objectFromEntries } from "@raven/shared/utils";
import { CATEGORY_TO_LABEL_MAP } from "../../../constants/constants";
import type { PnlChartSectionType, TimeRangeFinite } from "types/types";
import {
  PnlCategoriesEnum,
  PnlCategoriesEnumWithOptionalCategories,
} from "types/enums";
import { HIDDEN_CATEGORIES_OTHER_PROCESSES_PNL_CHART_PER_CUSTOMER } from "constants/customers";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";

export const NOT_SCHEDULED_TITLE = "Not Scheduled";
export const isTitleNoPercent = title =>
  [NOT_SCHEDULED_TITLE, "Uncollected"].includes(title);

export const rollupSingleProcessBreakdownCharts = (
  selectedCharts: PnlChartSectionType[][],
  hiddenPnlChartCategories: string[],
  isCustomerWhoUsesQualityLossPnlChart: boolean
): PnlChartSectionType[] => {
  let totalRollupTimeForPercentsCalc = 0;
  let biggestBarTime = 0;
  const rolledUpCharts = selectedCharts.reduce(
    (acc: PnlChartSectionType[], chartToRollUp: PnlChartSectionType[]) => {
      chartToRollUp.forEach(pnlCategorySubChart => {
        const associatedAccumulatorChart =
          acc.find(
            accPnlCategorySubChart =>
              accPnlCategorySubChart.title === pnlCategorySubChart.title
          ) || ({} as any);
        // add the charts together
        associatedAccumulatorChart.time += pnlCategorySubChart.time;
        associatedAccumulatorChart.count += 1;

        pnlCategorySubChart.bars.forEach(bar => {
          const barInAcc = associatedAccumulatorChart.bars?.find(
            accBar => accBar.title === bar.title
          );
          // initialize the bar if it doesn't exist
          if (!barInAcc) {
            associatedAccumulatorChart.bars = [
              ...(associatedAccumulatorChart.bars || []),
              {
                ...bar,
                percent: 0,
                count: 1,
                percentWidth: 0,
              },
            ];
          } else {
            // otherwise add the time + count
            barInAcc.time += bar.time;
            barInAcc.count += 1;
          }
          // track totalRollupTime to recalculate the bar widths/percents

          if (
            // these categories don't affect bar percents
            !["Uncollected", NOT_SCHEDULED_TITLE].includes(
              pnlCategorySubChart.title
            )
          ) {
            totalRollupTimeForPercentsCalc += bar.time;
          }
          if (
            // these categories don't affect bar widths
            ![
              "Producing",
              NOT_SCHEDULED_TITLE,
              ...hiddenPnlChartCategories.map(
                category => CATEGORY_TO_LABEL_MAP[category]
              ),
            ].includes(pnlCategorySubChart.title)
          ) {
            biggestBarTime = Math.max(
              biggestBarTime,
              barInAcc ? barInAcc.time : bar.time
            );
          }
        });
      });
      return acc;
    },
    [
      ...(isCustomerWhoUsesQualityLossPnlChart ? ["Quality"] : []),
      "Producing",
      "Planned",
      "Unplanned",
      "Waiting",
      "Unknown",
      NOT_SCHEDULED_TITLE,
      "Uncollected",
    ].map(title => {
      return {
        title,
        time: 0,
        percent: 0,
        count: 0,
        bars: [],
      } as PnlChartSectionType;
    })
  );
  // recalculate the rolled-up bar percents/widths
  rolledUpCharts.forEach(chart => {
    chart.bars.forEach(bar => {
      bar.percent = !totalRollupTimeForPercentsCalc
        ? 0 // if all uncollected time, return 0% instead of NaN
        : bar.time / totalRollupTimeForPercentsCalc;
      chart.percent = (chart.percent || 0) + bar.percent;
      bar.percentWidth = bar.time / biggestBarTime;
    });
  });
  return rolledUpCharts;
};

/** highlight Segments on the Timeline, Trend Chart, Pnl Chart.
 *
 * for accurate representation, charts only highlight themselves and those below them.
 *
 * @param isButterfly if true, highlight all segments (Trend Chart, Pnl Chart, Timeline)
 *
 * @param tag if passed, filter to only those of the tag (or pnl category)
 *
 * @param rangeToHighlight if passed, filter to only those touching the range
 *
 * @param processId if passed, filter timeline segments to only the timeline matching this process id
 *
 */
export const highlightTags = ({
  tag,
  doHighlight,
  isOverall,
  rangeToHighlight,
  processId,
}: {
  tag: string | null;
  doHighlight: boolean;
  isOverall: boolean;
  rangeToHighlight?: TimeRangeFinite | null;
  processId?: string | null;
}) => {
  const camelCaseTag = tag ? camelCase(tag) : null;

  // butterfly chart highlights everything below;
  // PnlSummaryChart only highlights timeline

  // (trend uses its own highlight function)
  const trendChartBarSelector = `.trendChartBar.${camelCaseTag}`;
  const machineBreakdownChartSelector = `.btnMachineBreakdownRow .pnlBarHorizontal.${camelCaseTag}`;
  const trendAndMachineBreakdownChartsSelector = `${trendChartBarSelector}, ${machineBreakdownChartSelector},`;

  const overallBarstackTooltipSelector = `.tooltipRow.tooltipRow-${camelCaseTag}`;

  const selector = processId
    ? // select only the process's timeline if processId is provided
      `.Timeline-${processId} .production-segment-${camelCaseTag}, .tag-highlight-${camelCaseTag}`
    : tag
      ? // highlight only tags if tag is provided
        `.tag-${camelCaseTag}, ${
          isOverall ? trendAndMachineBreakdownChartsSelector : ""
        } .production-segment-${camelCaseTag}, .tag-highlight-${camelCaseTag}, ${overallBarstackTooltipSelector}`
      : // else highlight all segments
        ".production-segment";

  // fire an event named "HighlightSegments" to highlight the segments, this will be handled in CheapCanvasProductionSegments to handle highlighting canvas segments
  document.dispatchEvent(
    new CustomEvent("HighlightSegments", {
      detail: {
        camelCaseTag,
        doHighlight,
        rangeToHighlight,
        isOverall,
      },
    })
  );

  const segmentsToHighlight = document.querySelectorAll<HTMLElement>(selector);

  if (segmentsToHighlight) {
    const filteredSegments =
      rangeToHighlight && doHighlight // if we're unhighlighting, no need to filter
        ? Array.from(segmentsToHighlight).filter((seg: HTMLElement) => {
            const segGe = Number(seg.dataset?.rangeGe);
            const segLt = Number(seg.dataset?.rangeLt);

            const isPnlDataRowCell = !segGe && !segLt;

            const { ge, lt } = rangeToHighlight;
            // highlight if the segment touches the range
            return (
              isPnlDataRowCell ||
              (segGe &&
                segLt &&
                // (range ge is inside the segment)
                ((segGe <= ge && ge < segLt) ||
                  // (range lt is inside the segment)
                  (segGe <= lt && lt < segLt) ||
                  // (segment is inside the range)
                  (ge <= segGe && segLt < lt)))
            );
          })
        : segmentsToHighlight;

    filteredSegments.forEach((seg: HTMLElement) => {
      if (doHighlight) {
        seg.classList?.add("highlightSegment");
      } else {
        seg.classList?.remove("highlightSegment");
      }
    });
  }
};

export function filterPnlChartsForSerialProcess({
  SPChartsArr,
  processIdsToKeep,
  hiddenPnlChartCategories = [],
  isCustomerWhoUsesQualityLossPnlChart,
}: {
  SPChartsArr: [string, PnlChartSectionType[]][];
  processIdsToKeep: string[];
  hiddenPnlChartCategories: string[];
  isCustomerWhoUsesQualityLossPnlChart: boolean;
}) {
  const selectedChartsObj = objectFromEntries(
    SPChartsArr.filter(([processId]) => processIdsToKeep.includes(processId))
  ) as { [machemetryId: string]: PnlChartSectionType[] };
  const selectedCharts: PnlChartSectionType[][] =
    Object.values(selectedChartsObj);
  return selectedCharts.length === 0
    ? []
    : rollupSingleProcessBreakdownCharts(
        selectedCharts,
        hiddenPnlChartCategories,
        isCustomerWhoUsesQualityLossPnlChart
      );
}

/** all serial process groups show an "Other Processes" PnL chart, which by default, only displays planned/unplanned */
const HIDDEN_CATEGORIES_OTHER_PROCESSES_PNL_CHART: (
  | string
  | PnlCategoriesEnum
  | PnlCategoriesEnumWithOptionalCategories
)[] = [
  PnlCategoriesEnum.producing,
  PnlCategoriesEnum.waiting,
  PnlCategoriesEnum.notScheduled,
  PnlCategoriesEnum.unknown,
  PnlCategoriesEnum.uncollected,
];

/**
 * Get custom hidden cateogires for other processes pnl chart
 * or use default
 */
export const useHiddenCategoriesForOtherProcessesPnlChart = () => {
  const cKey = useCustomerKey();
  return (
    HIDDEN_CATEGORIES_OTHER_PROCESSES_PNL_CHART_PER_CUSTOMER?.[cKey] ??
    HIDDEN_CATEGORIES_OTHER_PROCESSES_PNL_CHART
  );
};
